import { axios_server } from ".";
import { toast } from "sonner";
import axios from "axios";
import config from "@/config";
import { DBProvisionResponse, ProjectType } from "@/types/project.type";

export const handleClone = (data: any) =>
  new Promise<any>((resolve, reject) => {
    axios_server.post("/v1/projects", data).then(resolve).catch(reject);
  });

export const _getRepo = async ({
  full_name,
  installation_id,
}: {
  full_name: string;
  installation_id: string;
}) => {
  const { data } = await axios_server.get(
    `/v1/repos/github/repo?repoName=${full_name}&installationId=${installation_id}`,
  );
  return data;
};

export const createProject = (data: any) =>
  new Promise((resolve, reject) => {
    axios_server
      .post("/v1/projects", data)
      .then((res) => resolve(res))
      .catch((error) => reject(error));
  });

export const handleGetHistoryLog = ({
  projectID,
  logID,
}: {
  projectID: string;
  logID: string;
}) =>
  new Promise((resolve, reject) => {
    axios_server
      .get(`/v1/logs/${projectID}/${logID}`)
      .then((res) => resolve(res))
      .catch((err) => reject(err));
  });

export const handleRenameProject = ({
  id,
  name,
  authEnabled,
  buildCacheEnabled,
}: {
  id: string;
  name: string;
  authEnabled: boolean;
  buildCacheEnabled: boolean;
}) =>
  new Promise((resolve, reject) => {
    axios_server
      .post(`/v1/projects/${id}/redeploy`, {
        name,
        authEnabled,
        buildCacheEnabled,
      })
      .then((res) => resolve(res))
      .catch((err) => reject(err));
  });

export const updateDbConfig = async (
  id: string,
  payload: {
    name: string;
    password: string;
    configurations: ProjectType["specs"];
    whitelistedIps: string[];
  },
) => {
  try {
    const res = await axios_server.put(`/v1/projects/database/${id}`, payload);
    return res.data;
  } catch (error) {
    throw error;
  }
};

export const redeployProject = (id: string, data: any) =>
  new Promise<any>((resolve, reject) => {
    axios_server
      .post(`/v1/projects/${id}/redeploy`, data)
      .then((res) => {
        resolve(res);
      })
      .catch((err) => reject(err));
  });

export const deleteProject = (identifier: string) =>
  new Promise<any>((resolve, reject) => {
    axios_server
      .delete(`/v1/projects/${identifier}`)
      .then((res) => resolve(res))
      .catch((err) => reject(err));
  });

export const updateEnv = (
  id: string,
  projectId: string,
  data: { name: string; value: string },
) =>
  new Promise<any>((resolve, reject) => {
    axios_server
      .patch(`/v1/envs/${projectId}/${id}`, { environment: data })
      .then((res) => resolve(res))
      .catch((err) => reject(err));
  });

export const deleteEnv = (id: string, projectId: string) =>
  new Promise<any>((resolve, reject) => {
    axios_server
      .delete(`/v1/envs/${projectId}/${id}`)
      .then((res) => resolve(res))
      .catch((err) => reject(err));
  });

export const handleAddEnv = ({
  environments,
  id,
  options,
  environment,
}: {
  id: string;
  environments: { name: string; value: string }[];
  options?: { editor?: boolean };
  environment?: string;
}) =>
  new Promise((resolve, reject) => {
    axios_server
      .post(
        `/v1/envs/${id}/${environment || ""}${
          options?.editor ? "?editor=true" : ""
        }`,
        { environments },
      )
      .then((res) => resolve(res))
      .catch((err) => reject(err?.response || err?.request || err?.message));
  });

export const transferProject = (id: string, data: { teamId: string }) =>
  new Promise((resolve, reject) => {
    axios_server
      .post(`/v1/projects/transfer/${id}`, data)
      .then((res) => resolve(res))
      .catch((err) => reject(err));
  });

export const handleProjectMaintenance = ({
  id,
  maintenance,
}: {
  id: string;
  maintenance: boolean;
}) =>
  new Promise<any>((resolve, reject) => {
    axios_server
      .put(`/v1/projects/maintenance/${id}`, { maintenance })
      .then((res) => {
        resolve(res);
      })
      .catch((err) => reject(err));
  });

export const setProjectPassword = ({
  id,
  ...props
}: {
  id: string;
  teamId?: string;
  passwordEnabled?: boolean;
  password?: string;
}) =>
  new Promise<any>((resolve, reject) => {
    axios_server
      .put(`/v1/projects/password-protect/${id}`, { ...props })
      .then((res) => resolve(res))
      .catch((err) => reject(err));
  });

export const handleLinkRepoToProject = async (
  id: string,
  projectName: string,
  installationId: number | undefined,
) => {
  try {
    await axios_server.post(`/v1/projects/link/${id}`, {
      type: "repo",
      repo: {
        name: projectName,
        installationId: installationId,
        git: "github",
      },
    });
    toast.success("Successfully linked repository to project");
  } catch (error: any) {
    toast.error(error?.message || "An error occurred");
  }
};

export const handleSystemStatus = async () =>
  new Promise<any>((resolve, reject) => {
    axios
      .get(`https://api.instatus.com/v1/pages`, {
        headers: { Authorization: `Bearer ${config.INSTATUS_APIKEY}` },
      })
      .then((res) => resolve(res))
      .catch((err) => reject(err));
  });

export const createDB = async (data: any) => {
  try {
    const response = await axios_server.post<DBProvisionResponse>(
      "/v1/projects/database",
      data,
    );
    return response.data;
  } catch (error: any) {
    throw error;
  }
};
